/**
 * The end over the town (variant B of the end-screen prototype), without the DOM: what a sitting's answers came to,
 * the chips that hang over the lots that changed, and the one headline the sheet under the town leads with.
 *
 * - The tally counts the sitting's saved answers: right over scored cards only (a flip, a reveal, a skip, "I said
 *   it" and a spelling card never count), the words with a scored miss, and the words the replies say left the fog
 *   or climbed a material.
 * - The chips come from the town before and after (`GET /api/town`), one per lot at most: "new" for fog lifted,
 *   "↑ material" for a climb, "again" for a word missed and not moved, a lamp lit again when nothing else says so.
 * - The headline picks the most meaningful change: a first (`POST /api/learn/end`), else fog lifted, else houses
 *   climbed, else lamps lit again, else ivy cleared, else (with no miss either) an honest "nothing moved".
 *
 * Materials only; a box never shows.
 */
import { isScored } from '../ladder.generated';
import type { FirstKind } from '../types';
import { MATERIALS, townChanges, type Material, type OwnTown } from './own-town';
import { passed, relit, type SavedAnswer } from './round-plan';

/** A sitting's saved answers, in counts and words. */
export interface Tally {
  /** Scored cards passed (a scratch passes). */
  right: number;
  /** Scored cards answered. */
  scored: number;
  /** Keys with a scored miss this sitting. */
  missed: string[];
  /** Keys whose fog lifted, and that climbed a material, from the attempt replies. */
  fog: string[];
  climbed: string[];
  /** Each key's word as the app names it (Finglish first for Persian), for the lines that name them. */
  names: Record<string, string>;
}

export const emptyTally = (): Tally => ({ right: 0, scored: 0, missed: [], fog: [], climbed: [], names: {} });

/** The words with a scored miss this sitting, as named, in the order they were missed. */
export const missedWords = (t: Tally): string[] => t.missed.map((k) => t.names[k] ?? k);

/** One saved answer into the tally: its card type, the answer as sent and graded, and the server's reply. */
export function tallyAnswer(
  t: Tally,
  a: SavedAnswer & { type: string },
  key: string,
  word: string,
  reply: { leftFog?: true; climbed?: true },
): Tally {
  const out: Tally = { ...t, missed: [...t.missed], fog: [...t.fog], climbed: [...t.climbed], names: { ...t.names, [key]: word } };
  if (reply.leftFog && !out.fog.includes(key)) out.fog.push(key);
  if (reply.climbed && !out.climbed.includes(key)) out.climbed.push(key);
  if (a.spelling || !isScored(a)) return out;
  out.scored += 1;
  if (passed(a)) out.right += 1;
  else if (!out.missed.includes(key)) out.missed.push(key);
  return out;
}

/** `blocked`, `scratched` and `hit` are a raid's defence (v1 ticket 51): what a strike did to a building. */
export type ChipKind = 'new' | 'climbed' | 'again' | 'lamp' | 'blocked' | 'scratched' | 'hit' | 'repaired';

export interface EndChip {
  key: string;
  /** The word as the app names it (Finglish first for Persian). */
  text: string;
  kind: ChipKind;
  /** The material it climbed to (`climbed`). */
  material?: Material;
  /** The words after the name, when the chip's kind needs more than its own string (a hit's "−3"). */
  label?: string;
}

/**
 * One chip per changed lot among `keys`, the ones reached this sitting: fog lifted, then climbed (the higher
 * material first), then missed and not moved, then a lamp lit again with nothing else to say.
 */
export function endChips(before: OwnTown, after: OwnTown, keys: readonly string[], missed: readonly string[]): EndChip[] {
  const was = new Map(before.buildings.map((b) => [b.key, b]));
  const miss = new Set(missed);
  const out: EndChip[] = [];
  for (const b of after.buildings) {
    const a = was.get(b.key);
    if (!a) continue;
    const text = b.word.latin || b.word.text;
    // A crack mended (v1 ticket 22) shows on its lot even when the word was not asked: a full repair bar clears them all.
    if (a.cracked && !b.cracked) { out.push({ key: b.key, text, kind: 'repaired' }); continue; }
    if (!keys.includes(b.key)) continue;
    const up = MATERIALS.indexOf(b.material) - MATERIALS.indexOf(a.material);
    if (a.material === 'fog' && b.material !== 'fog') out.push({ key: b.key, text, kind: 'new' });
    else if (up > 0) out.push({ key: b.key, text, kind: 'climbed', material: b.material });
    else if (miss.has(b.key)) out.push({ key: b.key, text, kind: 'again' });
    else if (relit(a, b)) out.push({ key: b.key, text, kind: 'lamp' });
  }
  const rank: Record<ChipKind, number> = { new: 0, climbed: 1, repaired: 2, again: 3, lamp: 4, hit: 5, scratched: 6, blocked: 7 };
  const mat = (c: EndChip) => (c.material ? MATERIALS.indexOf(c.material) : 0);
  return out.sort((x, y) => rank[x.kind] - rank[y.kind] || mat(y) - mat(x));
}

/** What the sheet's headline and score line count. */
export interface EndSummary {
  right: number;
  scored: number;
  fog: number;
  climbed: number;
  /** Lamps lit again and ivy cleared (from the towns only: the replies don't say). */
  lamps: number;
  ivy: number;
  /** Cracks repaired (v1 ticket 22), from the towns. */
  repaired: number;
  /** Words with a scored miss (their chips say "again"). */
  missed: number;
}

/**
 * The summary: the fog lifted, climbed, lamp and ivy counts from the town before and after when both could be read
 * (what the learner sees built), else fog and climbs from the replies.
 */
export function summaryOf(t: Tally, towns?: { before: OwnTown | null; after: OwnTown | null; keys: readonly string[] }): EndSummary {
  const c = towns?.before && towns.after ? townChanges(towns.before, towns.after, towns.keys) : null;
  return {
    right: t.right, scored: t.scored,
    fog: c ? c.fog.length : t.fog.length, climbed: c ? c.climbed.length : t.climbed.length,
    lamps: c ? c.lamps.length : 0, ivy: c ? c.ivy.length : 0, repaired: c ? c.repaired.length : 0, missed: t.missed.length,
  };
}

export type Headline =
  | { kind: 'first'; first: FirstKind; word: string }
  | { kind: 'fog'; n: number }
  | { kind: 'climbed'; n: number }
  | { kind: 'repaired'; n: number }
  | { kind: 'lamps'; n: number }
  | { kind: 'ivy'; n: number }
  | { kind: 'none' };

/** The firsts from the most to the least: a gold house means more than a first word out of the fog. */
const FIRST_RANK: readonly FirstKind[] = ['gold', 'stone', 'line', 'wood', 'fog'];

/** The firsts in the order they show, the headline's first. */
export function rankFirsts<F extends { kind: FirstKind }>(firsts: readonly F[]): F[] {
  return [...firsts].sort((a, b) => FIRST_RANK.indexOf(a.kind) - FIRST_RANK.indexOf(b.kind));
}

/**
 * The one sentence the end leads with, or null when only misses happened (their "again" chips and the missed words'
 * line say it): "nothing moved" only when no chip at all can show.
 */
export function headlineOf(s: EndSummary | null, firsts: readonly { kind: FirstKind; word: string }[]): Headline | null {
  const top = rankFirsts(firsts)[0];
  if (top) return { kind: 'first', first: top.kind, word: top.word };
  if (s?.fog) return { kind: 'fog', n: s.fog };
  if (s?.climbed) return { kind: 'climbed', n: s.climbed };
  if (s?.repaired) return { kind: 'repaired', n: s.repaired };
  if (s?.lamps) return { kind: 'lamps', n: s.lamps };
  if (s?.ivy) return { kind: 'ivy', n: s.ivy };
  if (s?.missed) return null;
  return { kind: 'none' };
}
